import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mockCreators } from '../../../mocks/creators';
import { createCreatorUrl } from '../../../hooks/useNavigation';

const FeaturedCreators = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);

  const creators = mockCreators.map(c => ({
    id: c.id,
    slug: c.slug,
    name: c.name,
    role: c.role,
    country: c.country,
    avatar: c.avatar,
    coverImage: c.coverImage,
    followers:
      c.followers >= 1_000_000
        ? `${(c.followers / 1_000_000).toFixed(1)}M`
        : `${Math.floor(c.followers / 1_000)}K`,
    verified: c.verified,
    recentWork: c.recentWork,
    awards: c.awards,
    projects: c.projects,
    fanRating: c.fanRating,
    specialty:
      c.specialties[0] + (c.specialties[1] ? ' · ' + c.specialties[1] : ''),
    bio: c.bio,
  }));

  const spotlight = creators[activeIndex];
  const others = creators.filter((_, i) => i !== activeIndex);

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % creators.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused, creators.length]);

  return (
    <section>
      {/* Section Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-rose-500 text-white shadow-lg shadow-amber-500/20">
            <i className="ri-user-star-line text-lg"></i>
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Featured Creators</h2>
            <p className="text-xs text-gray-500">
              Talented artists shaping Arab cinema
            </p>
          </div>
        </div>
        <Link
          to="/creators"
          className="text-sm font-medium text-amber-600 hover:text-amber-700 flex items-center gap-1 whitespace-nowrap cursor-pointer"
        >
          View All
          <i className="ri-arrow-right-s-line"></i>
        </Link>
      </div>

      <div
        className="grid grid-cols-5 gap-4"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Spotlight Card - Left 3 cols */}
        <Link
          to={createCreatorUrl(spotlight.slug)}
          className="col-span-3 relative group rounded-2xl overflow-hidden cursor-pointer bg-gray-900"
        >
          <div className="w-full h-[380px] relative">
            <img
              src={spotlight.coverImage}
              alt={spotlight.name}
              className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105 opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/50 to-transparent"></div>
          </div>

          {/* Spotlight badge */}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="px-3 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-lg shadow-amber-500/30">
              <i className="ri-flashlight-line"></i>
              Spotlight
            </span>
          </div>

          {/* Creator info */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-end gap-4">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl">
                  <img
                    src={spotlight.avatar}
                    alt={spotlight.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {spotlight.verified && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center bg-amber-500 rounded-full border-2 border-gray-900">
                    <i className="ri-vip-crown-fill text-white text-[10px]"></i>
                  </div>
                )}
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 bg-white/10 backdrop-blur-md text-amber-300 text-xs font-medium rounded-md border border-white/10">
                    {spotlight.role}
                  </span>
                  <span className="px-2 py-0.5 bg-white/10 backdrop-blur-md text-white/70 text-xs font-medium rounded-md border border-white/10">
                    {spotlight.country}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-white tracking-tight mb-1">
                  {spotlight.name}
                </h3>
                <p className="text-sm text-gray-300 line-clamp-2 leading-relaxed max-w-md">
                  {spotlight.bio}
                </p>

                {/* Stats row */}
                <div className="flex items-center gap-5 mt-3">
                  <div className="flex items-center gap-1.5 text-white/80">
                    <i className="ri-user-follow-line text-sm text-amber-400"></i>
                    <span className="text-sm font-semibold">{spotlight.followers}</span>
                    <span className="text-xs text-gray-400">followers</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/80">
                    <i className="ri-film-line text-sm text-rose-400"></i>
                    <span className="text-sm font-semibold">{spotlight.projects}</span>
                    <span className="text-xs text-gray-400">projects</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/80">
                    <i className="ri-award-fill text-sm text-amber-400"></i>
                    <span className="text-sm font-semibold">{spotlight.awards}</span>
                    <span className="text-xs text-gray-400">awards</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/80">
                    <i className="ri-star-fill text-sm text-yellow-400"></i>
                    <span className="text-sm font-semibold">{spotlight.fanRating}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress dots */}
            <div className="flex items-center gap-1.5 mt-5">
              {creators.map((_, i) => (
                <button
                  key={i}
                  onClick={e => {
                    e.preventDefault();
                    setActiveIndex(i);
                  }}
                  className={`h-1 rounded-full transition-all duration-500 cursor-pointer ${
                    i === activeIndex
                      ? 'w-8 bg-amber-400'
                      : 'w-3 bg-white/25 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>
          </div>
        </Link>

        {/* Right Column - Compact Creator Cards */}
        <div className="col-span-2 flex flex-col gap-3">
          {others.slice(0, 4).map((creator, index) => (
            <Link
              key={creator.id}
              to={createCreatorUrl(creator.slug)}
              className="group relative flex items-center gap-3.5 p-3 bg-white rounded-xl border border-gray-100 hover:border-amber-200 hover:shadow-lg hover:shadow-amber-500/5 transition-all duration-300 cursor-pointer"
            >
              {/* Rank number */}
              <div className="absolute -left-0.5 top-1/2 -translate-y-1/2 w-5 h-8 flex items-center justify-center bg-gradient-to-b from-amber-400 to-rose-400 text-white text-[10px] font-extrabold rounded-r-md shadow-sm">
                {index + 1}
              </div>

              {/* Avatar */}
              <div className="relative flex-shrink-0 ml-3">
                <div className="w-14 h-14 rounded-xl overflow-hidden border border-gray-100 group-hover:border-amber-200 transition-colors">
                  <img
                    src={creator.avatar}
                    alt={creator.name}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                {creator.verified && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 flex items-center justify-center bg-amber-500 rounded-full border-2 border-white">
                    <i className="ri-check-line text-white text-[8px]"></i>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <h4 className="text-sm font-bold text-gray-900 truncate group-hover:text-amber-700 transition-colors">
                    {creator.name}
                  </h4>
                </div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[11px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                    {creator.role}
                  </span>
                  <span className="text-[11px] text-gray-400">{creator.specialty}</span>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-follow-line text-amber-500"></i>
                    <span className="font-semibold text-gray-700">{creator.followers}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-film-line text-rose-400"></i>
                    <span className="font-semibold text-gray-700">{creator.projects}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-500"></i>
                    <span className="font-semibold text-gray-700">{creator.fanRating}</span>
                  </span>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50 group-hover:bg-amber-50 transition-colors">
                <i className="ri-arrow-right-s-line text-gray-400 group-hover:text-amber-600 transition-colors"></i>
              </div>
            </Link>
          ))}

          {/* View All Mini Card */}
          <Link
            to="/creators"
            className="flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-amber-50 to-rose-50 rounded-xl border border-amber-100 hover:border-amber-200 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex -space-x-2">
              {creators.slice(0, 3).map(c => (
                <div key={c.id} className="w-7 h-7 rounded-full overflow-hidden border-2 border-white">
                  <img src={c.avatar} alt={c.name} className="w-full h-full object-cover object-top" />
                </div>
              ))}
            </div>
            <span className="text-sm font-semibold text-amber-700 group-hover:text-amber-800 whitespace-nowrap">
              Explore {creators.length}+ Creators
            </span>
            <i className="ri-arrow-right-line text-amber-500 group-hover:translate-x-0.5 transition-transform"></i>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedCreators;
